import React, { useEffect, useMemo, useState } from "react";
import { CCard, CCardBody, CCardHeader, CSpinner } from "@coreui/react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import "highcharts/modules/drilldown";

import { apiClient } from "services/Api";

const PLANNED_SERIES = "Custos Planeados";
const REAL_SERIES = "Custos Reais";
const RATIO_SERIES = "Execução (%)";

const MONTH_NAMES = [
  "Jan",
  "Fev",
  "Mar",
  "Abr",
  "Mai",
  "Jun",
  "Jul",
  "Ago",
  "Set",
  "Out",
  "Nov",
  "Dez",
];

const formatCurrency = (value) =>
  `${Number(value || 0).toLocaleString("pt-PT", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} €`;

const formatPercent = (value) =>
  `${Number(value).toLocaleString("pt-PT", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })}%`;

// null (rather than 0/Infinity) when there's nothing to divide by, so the
// execution line simply skips that point instead of showing a misleading value.
const computeRatio = (real, planned) => {
  if (!planned) return null;
  return Number(((real / planned) * 100).toFixed(1));
};

const buildDrilldownOptions = (projects) => {
  const plannedTopData = projects.map((project) => ({
    name: project.name,
    y: project.planned_cost,
    drilldown: project.years.length ? `planned:${project.id}` : undefined,
  }));
  const realTopData = projects.map((project) => ({
    name: project.name,
    y: project.real_cost,
    drilldown: project.years.length ? `real:${project.id}` : undefined,
  }));
  const ratioTopData = projects.map((project) => ({
    name: project.name,
    y: computeRatio(project.real_cost, project.planned_cost),
    drilldown: project.years.length ? `ratio:${project.id}` : undefined,
  }));

  const drilldownSeries = [];

  projects.forEach((project) => {
    if (!project.years.length) return;

    drilldownSeries.push({
      id: `planned:${project.id}`,
      name: PLANNED_SERIES,
      data: project.years.map((year) => ({
        name: String(year.year),
        y: year.planned_cost,
        drilldown: year.months.length
          ? `planned:${project.id}:${year.year}`
          : undefined,
      })),
    });
    drilldownSeries.push({
      id: `real:${project.id}`,
      name: REAL_SERIES,
      data: project.years.map((year) => ({
        name: String(year.year),
        y: year.real_cost,
        drilldown: year.months.length
          ? `real:${project.id}:${year.year}`
          : undefined,
      })),
    });
    drilldownSeries.push({
      id: `ratio:${project.id}`,
      name: RATIO_SERIES,
      type: "line",
      yAxis: 1,
      data: project.years.map((year) => ({
        name: String(year.year),
        y: computeRatio(year.real_cost, year.planned_cost),
        drilldown: year.months.length
          ? `ratio:${project.id}:${year.year}`
          : undefined,
      })),
    });

    project.years.forEach((year) => {
      if (!year.months.length) return;

      drilldownSeries.push({
        id: `planned:${project.id}:${year.year}`,
        name: PLANNED_SERIES,
        data: year.months.map((month) => ({
          name: MONTH_NAMES[month.month - 1],
          y: month.planned_cost,
        })),
      });
      drilldownSeries.push({
        id: `real:${project.id}:${year.year}`,
        name: REAL_SERIES,
        data: year.months.map((month) => ({
          name: MONTH_NAMES[month.month - 1],
          y: month.real_cost,
        })),
      });
      drilldownSeries.push({
        id: `ratio:${project.id}:${year.year}`,
        name: RATIO_SERIES,
        type: "line",
        yAxis: 1,
        data: year.months.map((month) => ({
          name: MONTH_NAMES[month.month - 1],
          y: computeRatio(month.real_cost, month.planned_cost),
        })),
      });
    });
  });

  return {
    chart: { type: "column" },
    title: { text: undefined },
    credits: { enabled: false },
    exporting: { enabled: false },
    xAxis: {
      type: "category",
    },
    yAxis: [
      {
        title: { text: "Custo (€)" },
      },
      {
        title: { text: "Execução (%)" },
        opposite: true,
        labels: { format: "{value}%" },
        plotLines: [
          {
            value: 100,
            color: "#9aa0a6",
            dashStyle: "Dash",
            width: 1,
            zIndex: 4,
          },
        ],
      },
    ],
    legend: { enabled: true },
    tooltip: {
      shared: true,
      pointFormatter: function () {
        const isRatio = this.series.name === RATIO_SERIES;
        const formattedValue =
          this.y == null
            ? "—"
            : isRatio
              ? formatPercent(this.y)
              : formatCurrency(this.y);
        return `<span style="color:${this.color}">●</span> ${this.series.name}: <b>${formattedValue}</b><br/>`;
      },
    },
    plotOptions: {
      column: {
        pointPadding: 0.1,
        borderWidth: 0,
      },
      line: {
        marker: { enabled: true },
        dataLabels: {
          enabled: true,
          formatter: function () {
            return this.y == null ? "" : formatPercent(this.y);
          },
        },
      },
    },
    series: [
      { name: PLANNED_SERIES, data: plannedTopData },
      { name: REAL_SERIES, data: realTopData },
      { name: RATIO_SERIES, type: "line", yAxis: 1, data: ratioTopData },
    ],
    drilldown: {
      allowPointDrilldown: false,
      breadcrumbs: { showFullPath: true },
      series: drilldownSeries,
    },
  };
};

const ProjectCostsDrilldownChart = ({ clientId, noIncentive, title }) => {
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiClient
      .get(`/projects/management/${clientId}/costs-drilldown/`, {
        params: { no_incentive: noIncentive },
      })
      .then((response) => {
        if (cancelled) return;
        setProjects(response.data?.projects || []);
      })
      .catch((error) => {
        console.error("Erro ao carregar custos do projeto:", error);
        if (!cancelled) setProjects([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [clientId, noIncentive]);

  const chartOptions = useMemo(() => buildDrilldownOptions(projects), [projects]);

  return (
    <CCard className="mb-3">
      <CCardHeader>
        <strong>{title}</strong>
      </CCardHeader>
      <CCardBody>
        {loading ? (
          <div className="d-flex justify-content-center py-4">
            <CSpinner />
          </div>
        ) : projects.length === 0 ? (
          <div className="text-muted text-center py-4">
            Sem dados para apresentar.
          </div>
        ) : (
          <HighchartsReact highcharts={Highcharts} options={chartOptions} />
        )}
      </CCardBody>
    </CCard>
  );
};

export default ProjectCostsDrilldownChart;
